import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import TopNavMenu from './top-nav-menu.jsx';
import { slideToggle } from './../../composables/slideToggle.js';
import { slideUp } from './../../composables/slideUp.js';

function handlePageLoadMenuFocus() {
	const topNav = document.querySelector('.app-top-nav');
	const targetMenu = topNav?.querySelector('.menu');
	if (!topNav || !targetMenu) {
		return;
	}

	const bodyStyle = window.getComputedStyle(document.body);
	const viewWidth = topNav.clientWidth;
	const menuItems = [...targetMenu.querySelectorAll(':scope > .menu-item:not(.menu-control)')];
	const controlStart = targetMenu.querySelector('.menu-control.menu-control-start');
	const controlEnd = targetMenu.querySelector('.menu-control.menu-control-end');
	let previousWidth = 0;
	let fullWidth = 0;
	let foundActive = false;

	menuItems.forEach((item) => {
		fullWidth += item.clientWidth;
		if (!foundActive) {
			previousWidth += item.clientWidth;
		}
		if (item.classList.contains('active')) {
			foundActive = true;
		}
	});

	const isOverflowing = fullWidth >= viewWidth;
	const activeItemIsOverflowing = previousWidth >= viewWidth;
	controlEnd?.classList.toggle('show', isOverflowing && previousWidth !== fullWidth);
	controlStart?.classList.toggle('show', isOverflowing && activeItemIsOverflowing);

	if (activeItemIsOverflowing) {
		const controlWidth = controlEnd?.clientWidth || 0;
		const offset = previousWidth - viewWidth + controlWidth;
		if (bodyStyle.getPropertyValue('direction') === 'rtl') {
			targetMenu.style.marginRight = `-${offset}px`;
		} else {
			targetMenu.style.marginLeft = `-${offset}px`;
		}
	}
}

function handleTopNavSubMenu() {
	const cleanups = [];
	const toggleMenus = (menus, forMobile = false) => {
		menus.forEach((menu) => {
			const handleClick = (e) => {
				e.preventDefault();
				if (forMobile && document.body.clientWidth >= 768) {
					return;
				}

				const target = menu.nextElementSibling;
				menus.forEach((otherMenu) => {
					const otherTarget = otherMenu.nextElementSibling;
					if (otherTarget !== target) {
						slideUp(otherTarget);
						otherTarget.closest('.menu-item').classList.remove('expand');
						otherTarget.closest('.menu-item').classList.add('closed');
					}
				});
				slideToggle(target);
			};

			menu.addEventListener('click', handleClick);
			cleanups.push(() => menu.removeEventListener('click', handleClick));
		});
	};

	const menuBaseSelector = '.app-top-nav .menu > .menu-item.has-sub';
	const submenuBaseSelector = ' > .menu-submenu > .menu-item.has-sub';
	toggleMenus([...document.querySelectorAll(`${menuBaseSelector} > .menu-link`)], true);
	toggleMenus([...document.querySelectorAll(`${menuBaseSelector}${submenuBaseSelector} > .menu-link`)]);
	toggleMenus([...document.querySelectorAll(`${menuBaseSelector}${submenuBaseSelector}${submenuBaseSelector} > .menu-link`)]);

	return () => cleanups.forEach((cleanup) => cleanup());
}

function handleTopNavDrag() {
	const container = document.querySelector('.app-top-nav');
	const menu = container?.querySelector('.menu');
	if (!container || !menu) {
		return () => {};
	}

	let startX = 0;
	let scrollLeft = 0;
	let mouseDown = false;

	const handleStart = (e) => {
		const pageX = e.type === 'touchstart' ? e.targetTouches[0].pageX : e.pageX;
		startX = pageX;
		scrollLeft = parseInt(menu.style.marginLeft || '0', 10);
		mouseDown = true;
	};
	const handleEnd = () => {
		mouseDown = false;
	};
	const handleMove = (e) => {
		if (!mouseDown || window.innerWidth < 768) {
			return;
		}
		const pageX = e.type === 'touchmove' ? e.targetTouches[0].pageX : e.pageX;
		const menuItems = [...menu.querySelectorAll('.menu-item:not(.menu-control)')];
		const menuWidth = menuItems.reduce((width, item) => width + item.offsetWidth, 0);
		const maxScroll = container.offsetWidth - menuWidth;
		const marginLeft = Math.min(0, Math.max(maxScroll, scrollLeft + pageX - startX));
		const controlStart = menu.querySelector('.menu-control.menu-control-start');
		const controlEnd = menu.querySelector('.menu-control.menu-control-end');

		e.preventDefault();
		menu.style.marginLeft = `${marginLeft}px`;
		controlStart?.classList.toggle('show', marginLeft < 0 && menuWidth > container.offsetWidth);
		controlEnd?.classList.toggle('show', marginLeft > maxScroll && menuWidth > container.offsetWidth);
	};

	container.addEventListener('mousedown', handleStart);
	container.addEventListener('touchstart', handleStart, { passive: false });
	container.addEventListener('mouseup', handleEnd);
	container.addEventListener('mouseleave', handleEnd);
	container.addEventListener('touchend', handleEnd);
	container.addEventListener('mousemove', handleMove);
	container.addEventListener('touchmove', handleMove, { passive: false });

	return () => {
		container.removeEventListener('mousedown', handleStart);
		container.removeEventListener('touchstart', handleStart);
		container.removeEventListener('mouseup', handleEnd);
		container.removeEventListener('mouseleave', handleEnd);
		container.removeEventListener('touchend', handleEnd);
		container.removeEventListener('mousemove', handleMove);
		container.removeEventListener('touchmove', handleMove);
	};
}

function TopNav() {
	const location = useLocation();

	useEffect(() => {
		const submenuCleanup = handleTopNavSubMenu();
		const dragCleanup = handleTopNavDrag();
		const handleResize = () => {
			const topNav = document.querySelector('.app-top-nav');
			const menu = topNav?.querySelector('.menu');
			if (window.innerWidth >= 768) {
				topNav?.removeAttribute('style');
				menu?.removeAttribute('style');
				menu?.querySelectorAll('.menu-submenu').forEach((submenu) => submenu.removeAttribute('style'));
				handlePageLoadMenuFocus();
			}
		};

		handlePageLoadMenuFocus();
		window.addEventListener('resize', handleResize);

		return () => {
			submenuCleanup();
			dragCleanup();
			window.removeEventListener('resize', handleResize);
		};
	}, [location.pathname]);

	return (
		<div id="appTopNav" className="app-top-nav" data-bs-theme="dark">
			<TopNavMenu />
		</div>
	);
}

export default TopNav;
